import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Users, UtensilsCrossed, Save, ChefHat, Home } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AdminLayout } from "@/components/admin/AdminLayout";

const PlatformSettings = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [platformFee, setPlatformFee] = useState("");
  const [payoutDelay, setPayoutDelay] = useState("");

  const { data: settings } = useQuery({
    queryKey: ['platform-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('*')
        .single();

      if (error) throw error;
      return data;
    }
  });

  useEffect(() => {
    if (settings) {
      setPlatformFee(settings.platform_fee_percentage.toString());
      setPayoutDelay(settings.payout_delay_days.toString());
    }
  }, [settings]);

  const handleSave = async () => {
    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('platform_settings')
        .update({
          platform_fee_percentage: parseFloat(platformFee),
          payout_delay_days: parseInt(payoutDelay),
        })
        .eq('id', settings?.id);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['platform-settings'] });
      toast({
        title: "Settings updated",
        description: "Platform settings have been updated successfully.",
      });
    } catch (error) {
      console.error('Error updating settings:', error);
      toast({
        title: "Error",
        description: "Failed to update settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSwitchToFoodieMode = () => {
    navigate('/profile');
    toast({
      title: "Switched to Foodie Mode",
      description: "You are now in Foodie Mode",
      duration: 1800,
    });
  };

  const handleSwitchToChefMode = () => {
    navigate('/seller/profile');
    toast({
      title: "Switched to Chef Mode",
      description: "You are now in Chef Mode",
      duration: 1800,
    });
  };

  return (
    <AdminLayout title="Settings">
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="p-4 hover:bg-gray-50 cursor-pointer" onClick={() => navigate('/admin/users')}>
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-primary" />
              <div>
                <h3 className="font-medium text-black">Users</h3>
                <p className="text-sm text-gray-600">Manage platform users</p>
              </div>
            </div>
          </Card>

          <Card className="p-4 hover:bg-gray-50 cursor-pointer" onClick={() => navigate('/admin/meals')}>
            <div className="flex items-center gap-3">
              <UtensilsCrossed className="h-5 w-5 text-primary" />
              <div>
                <h3 className="font-medium text-black">Meals</h3>
                <p className="text-sm text-gray-600">Manage meal listings</p>
              </div>
            </div>
          </Card>
        </div>

        <Card className="p-6 space-y-4">
          <h2 className="text-lg font-medium text-black">Platform Settings</h2>
          
          <div className="space-y-2">
            <label className="text-sm font-medium text-black">Platform Fee (%)</label>
            <Input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={platformFee}
              onChange={(e) => setPlatformFee(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-black">Payout Delay (days)</label>
            <Input
              type="number"
              min="0"
              value={payoutDelay}
              onChange={(e) => setPayoutDelay(e.target.value)}
            />
          </div>

          <Button 
            className="w-full" 
            onClick={handleSave}
            disabled={isLoading}
          >
            {isLoading ? (
              "Saving..."
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </Card>

        <div className="flex flex-col sm:flex-row gap-4">
          <Button 
            variant="outline" 
            className="flex-1 flex items-center gap-2"
            onClick={handleSwitchToFoodieMode}
          >
            <Home className="h-5 w-5" />
            Switch to Foodie Mode
          </Button>
          
          <Button 
            variant="outline" 
            className="flex-1 flex items-center gap-2"
            onClick={handleSwitchToChefMode}
          >
            <ChefHat className="h-5 w-5" />
            Switch to Chef Mode
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default PlatformSettings;
